import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LOGIN_STATES } from './useLogin.types';
import type { LoginState } from './useLogin.types';

export { LOGIN_STATES } from './useLogin.types';
export type { LoginState } from './useLogin.types';

export function useLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  const [currentState, setCurrentState] = useState<LoginState>(LOGIN_STATES.DEFAULT);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const next = useMemo(
    () => new URLSearchParams(location.search).get('next') || '/',
    [location.search]
  );
  const sessionExpired = useMemo(
    () => new URLSearchParams(location.search).get('session_expired') === '1',
    [location.search]
  );

  const isEmailValid = email.includes('@') && email.includes('.');
  const isPasswordFilled = password.length >= 1;
  const isFormValid = isEmailValid && isPasswordFilled;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;
    setIsSubmitting(true);
    const res = await login(email, password);
    setIsSubmitting(false);
    if (res.ok) {
      navigate(next, { replace: true });
      return;
    }
    const reason = (res as { reason?: string }).reason as
      | 'INVALID_CREDENTIALS'
      | 'ACCOUNT_BLOCKED'
      | 'ACCOUNT_DISABLED'
      | 'NOT_ACTIVATED'
      | 'UNKNOWN';
    if (reason === 'INVALID_CREDENTIALS') setCurrentState(LOGIN_STATES.INVALID_CREDENTIALS);
    else if (reason === 'ACCOUNT_BLOCKED') setCurrentState(LOGIN_STATES.ACCOUNT_BLOCKED);
    else if (reason === 'ACCOUNT_DISABLED') setCurrentState(LOGIN_STATES.ACCOUNT_DISABLED);
    else if (reason === 'NOT_ACTIVATED') setCurrentState(LOGIN_STATES.NOT_ACTIVATED);
    else setCurrentState(LOGIN_STATES.INVALID_CREDENTIALS);
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    currentState,
    isSubmitting,
    isEmailValid,
    isFormValid,
    next,
    sessionExpired,
    isAuthenticated,
    navigate,
    handleSubmit,
  };
}
